/**
 * IT training: one lesson block → React. Text goes through the app's
 * `<Markdown>` (React elements only, links allow-listed, no HTML sink); the
 * rest is built here from parsed data, so a lesson file can never inject markup.
 */

import {
  ArrowRight,
  Icon,
  type IconComponent,
  Image as ImageIcon,
  Info,
  Lightbulb,
  MessageSquare,
  Monitor,
  MousePointerClick,
  TriangleAlert,
} from "@norican/icons";
import { Badge, Button, Card, type CardProps, Typography } from "@norican/react";
import { Link } from "react-router";

import { Markdown } from "@/components/common";
import { useT } from "@/i18n";

import type { CalloutTone, LessonBlock, WherePlace } from "./lib/lessons";
import { LessonWidgetBlock } from "./sla-widgets";

const CALLOUTS: Readonly<
  Record<CalloutTone, { icon: IconComponent; tone: NonNullable<CardProps["accentTone"]> }>
> = {
  tip: { icon: Lightbulb, tone: "accent" },
  warning: { icon: TriangleAlert, tone: "warning" },
  note: { icon: Info, tone: "neutral" },
  chat: { icon: MessageSquare, tone: "accent" },
};

const WHERE: Readonly<Record<WherePlace, IconComponent>> = {
  ivanti: Monitor,
  helpdesk: MousePointerClick,
};

function Callout({ tone, text }: { tone: CalloutTone; text: string }) {
  const { t } = useT("tickets");
  const look = CALLOUTS[tone];
  return (
    <Card
      variant="subtle"
      padding="md"
      accent="left"
      accentTone={look.tone}
      className="flex flex-col gap-2"
    >
      <span className="flex items-center gap-2">
        <Icon icon={look.icon} size="sm" tone={tone === "warning" ? "warning" : "accent"} />
        <Typography as="span" variant="label">
          {t(`itAdmin.training.callout.${tone}`)}
        </Typography>
      </span>
      <Markdown>{text}</Markdown>
      {tone === "chat" ? (
        <span>
          <Button
            size="sm"
            variant="outline"
            render={<Link to="/ask" />}
            endIcon={<Icon icon={ArrowRight} size="sm" />}
          >
            {t("itAdmin.training.openChat")}
          </Button>
        </span>
      ) : null}
    </Card>
  );
}

function Where({ place, text }: { place: WherePlace; text: string }) {
  const { t } = useT("tickets");
  return (
    <div className="bg-bg-subtle border-border flex flex-col gap-2 rounded-md border px-3 py-2 sm:flex-row sm:items-start sm:gap-3">
      <Badge
        tone={place === "ivanti" ? "neutral" : "accent"}
        variant="subtle"
        size="sm"
        className="shrink-0 self-start"
      >
        <span className="inline-flex items-center gap-1">
          <Icon icon={WHERE[place]} size="xs" />
          {t(`itAdmin.training.where.${place}`)}
        </span>
      </Badge>
      <Markdown className="min-w-0">{text}</Markdown>
    </div>
  );
}

/** A screenshot, or its placeholder until the file names one. */
function Screenshot({ alt, src }: { alt: string; src: string | null }) {
  const { t } = useT("tickets");
  if (src === null) {
    return (
      <div
        role="img"
        aria-label={alt}
        data-todo="screenshot"
        className="border-border text-fg-muted flex flex-col items-center gap-1 rounded-md border border-dashed px-4 py-6 text-center"
      >
        <Icon icon={ImageIcon} size="md" />
        <Typography as="span" variant="body-sm">
          {alt}
        </Typography>
        <Typography as="span" variant="caption" tone="muted">
          {t("itAdmin.training.screenshotTodo")}
        </Typography>
      </div>
    );
  }
  return (
    <figure className="flex flex-col gap-1">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="border-border-subtle h-auto max-w-full rounded-md border"
      />
      <Typography as="figcaption" variant="caption" tone="muted">
        {alt}
      </Typography>
    </figure>
  );
}

export function LessonBlocks({ blocks }: { blocks: readonly LessonBlock[] }) {
  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, index) => {
        const key = `${block.kind}-${index}`;
        switch (block.kind) {
          case "markdown":
            return <Markdown key={key}>{block.text}</Markdown>;
          case "image":
            return <Screenshot key={key} alt={block.alt} src={block.src} />;
          case "callout":
            return <Callout key={key} tone={block.tone} text={block.text} />;
          case "where":
            return <Where key={key} place={block.place} text={block.text} />;
          case "widget":
            return <LessonWidgetBlock key={key} name={block.name} />;
        }
      })}
    </div>
  );
}
